// /advisory-board — named clinicians who review the engine + content.
// Walter (retired internist) demanded this in round-2: "show me the
// bench, not the spokesperson". Mei (biotech PhD) implied the same.
//
// CLAUDE-RESEARCH-GAP: The geriatrician MD and clinical pharmacology
// PhD entries below are placeholders that Matt must replace with real
// recruited clinicians before public launch. Dr. Marlee Grounds is the
// only confirmed advisor. Until the other two seats are filled, do not
// link this page from the public marketing site — only from inside the
// app where users already have account context.
// Suggested next research: identify candidates via the AGS member
// directory + ASCP (American Society of Consultant Pharmacists) board.
// Stipend budget needs to come from V1.1 P&L; consult Matt before
// reaching out.
// Priority: high. Owner: Matt.

import { Link } from 'react-router-dom';
import { AppPage } from '../layouts/AppPage';

interface Advisor {
  name: string;
  credentials: string;
  role: string;
  affiliation: string;
  bio: string;
  reviews: string[];
  confirmed: boolean;
}

const ADVISORS: Advisor[] = [
  {
    name: 'Dr. Marlee Grounds',
    credentials: 'PharmD, BCGP',
    role: 'Chief Medical Officer + lead engine reviewer',
    affiliation: 'OTCHealthMart (CMO)',
    bio: 'Board-Certified Geriatric Pharmacist (BCGP). Reviews every clinical engine release and the AI chat system prompt before deploy.',
    reviews: [
      'Clinical engine, every release',
      'Siggy chat system prompt',
      'Drug Facts label summaries',
      'Doctor Visit Summary PDF template',
    ],
    confirmed: true,
  },
  {
    name: 'Geriatrician MD, seat open',
    credentials: 'MD, internal medicine + geriatrics fellowship',
    role: 'Independent reviewer for STOPP/START + Beers',
    affiliation: 'TBD, recruiting from AGS member network',
    bio: 'Recruitment in progress; seat to be filled before public V1.1 launch.',
    reviews: ['STOPP/START criteria mapping', 'Severity rubric calibration'],
    confirmed: false,
  },
  {
    name: 'Clinical pharmacology PhD, seat open',
    credentials: 'PhD, clinical pharmacology',
    role: 'Independent reviewer for DDI graph + ototoxicity catalogue',
    affiliation: 'TBD',
    bio: 'Recruitment in progress; seat to be filled before public V1.1 launch.',
    reviews: ['DDI graph traversal logic', 'Ototoxicity catalogue expansion'],
    confirmed: false,
  },
];

export function AdvisoryBoard(): JSX.Element {
  return (
    <AppPage title="Medical Advisory Board" back={{ to: '/methodology', label: 'Methodology' }}>
      <section className="account-section">
        <p style={{ fontSize: 16, margin: 0 }}>
          Named clinicians review our safety engine. Each one signs off on what they review. We
          check the lists with them every three months.
        </p>
        <p style={{ marginTop: 12, fontSize: 14, color: 'var(--color-text-secondary)' }}>
          <Link to="/methodology" className="account-link" style={{ fontWeight: 600 }}>
            See the source list.
          </Link>
        </p>
      </section>

      <section className="account-section" style={{ display: 'grid', gap: 14 }}>
        {ADVISORS.map((a) => (
          <article
            key={a.name}
            style={{
              padding: 16,
              border: `1.5px solid ${a.confirmed ? 'var(--color-border)' : 'var(--color-warning, #fbbf24)'}`,
              borderRadius: 'var(--radius-md)',
              background: a.confirmed
                ? 'var(--color-surface-primary)'
                : 'var(--color-surface-secondary)',
            }}
          >
            <header style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{a.name}</h3>
              <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
                {a.credentials}
              </span>
              {!a.confirmed ? (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    background: '#fef3c7',
                    color: '#78350f',
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}
                >
                  RECRUITING
                </span>
              ) : null}
            </header>
            <p style={{ margin: '8px 0 0', fontSize: 15, fontStyle: 'italic' }}>{a.role}</p>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--color-text-secondary)' }}>
              {a.affiliation}
            </p>
            <p style={{ margin: '10px 0 0', fontSize: 15 }}>{a.bio}</p>
            <p style={{ margin: '10px 0 4px', fontSize: 14, fontWeight: 600 }}>Reviews:</p>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14 }}>
              {a.reviews.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </article>
        ))}
      </section>

      <section className="account-section">
        <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0 }}>
          MedReview seeks two additional advisors (geriatrician MD + clinical pharmacology PhD) to
          fill the rotational independent-review seats before public V1.1 launch. Interested? Email{' '}
          <a href="mailto:advisors@otchealthmart.com" className="account-link">
            advisors@otchealthmart.com
          </a>
          .
        </p>
      </section>
    </AppPage>
  );
}
